// Sign-up Activation v2 — milestone-timing dashboard.
// One row per (user, company) active pair. Each milestone column is the
// earliest UTC date the user hit that stage; null means never. The page
// computes "days since signup" client-side so histograms are signup-date-
// agnostic.

export const SIGNUP_FUNNEL_V2_SQL = `
WITH calendar_first AS (
    SELECT user_external_id, company_external_id,
           MIN(created_date_utc) AS first_calendar_at
    FROM public.calendar
    GROUP BY user_external_id, company_external_id
),
bot_first AS (
    SELECT mb.user_external_id, mb.company_external_id,
           MIN(mb.join_date_utc) AS first_bot_meeting_at
    FROM public.meeting_bot mb
    JOIN public.meeting m ON m.external_id = mb.meeting_external_id
    WHERE mb.join_date_utc IS NOT NULL
      AND m.is_demo = false
      AND m.deleted_date_utc IS NULL
    GROUP BY mb.user_external_id, mb.company_external_id
),
transcript_first AS (
    SELECT t.user_external_id, t.company_external_id,
           MIN(t.created_date_utc) AS first_transcript_at
    FROM public.meeting_transcription t
    JOIN public.meeting m ON m.external_id = t.meeting_external_id
    WHERE m.is_demo = false
      AND m.deleted_date_utc IS NULL
      AND t.deleted_date_utc IS NULL
    GROUP BY t.user_external_id, t.company_external_id
),
synopsis_per_meeting AS (
    SELECT s.user_external_id, s.company_external_id,
           s.meeting_external_id,
           MIN(s.created_date_utc) AS first_synopsis_for_meeting_at
    FROM public.meeting_synopsis s
    JOIN public.meeting m ON m.external_id = s.meeting_external_id
    WHERE m.is_demo = false
      AND m.deleted_date_utc IS NULL
    GROUP BY s.user_external_id, s.company_external_id, s.meeting_external_id
),
synopsis_meeting_ordered AS (
    SELECT user_external_id, company_external_id,
           first_synopsis_for_meeting_at,
           ROW_NUMBER() OVER (
               PARTITION BY user_external_id, company_external_id
               ORDER BY first_synopsis_for_meeting_at
           ) AS meeting_rank
    FROM synopsis_per_meeting
),
synopsis_milestones AS (
    SELECT user_external_id, company_external_id,
           MIN(first_synopsis_for_meeting_at) FILTER (WHERE meeting_rank = 1) AS first_synopsis_at,
           MIN(first_synopsis_for_meeting_at) FILTER (WHERE meeting_rank = 2) AS second_synopsis_at
    FROM synopsis_meeting_ordered
    GROUP BY user_external_id, company_external_id
),
chat_first AS (
    SELECT c.user_external_id, c.company_external_id,
           MIN(c.created_date_utc) AS first_meeting_chat_at
    FROM public.chat c
    LEFT JOIN public.meeting          m_direct  ON m_direct.external_id  = c.meeting_external_id
    LEFT JOIN public.meeting_synopsis s         ON s.external_id         = c.synopsis_external_id
    LEFT JOIN public.meeting          m_via_syn ON m_via_syn.external_id = s.meeting_external_id
    WHERE c.deleted_date_utc IS NULL
      AND (c.meeting_external_id IS NOT NULL OR c.synopsis_external_id IS NOT NULL)
      AND (
            (m_direct.external_id  IS NOT NULL AND m_direct.is_demo  = false AND m_direct.deleted_date_utc  IS NULL)
         OR (m_via_syn.external_id IS NOT NULL AND m_via_syn.is_demo = false AND m_via_syn.deleted_date_utc IS NULL)
      )
    GROUP BY c.user_external_id, c.company_external_id
),
custom_template_first AS (
    SELECT t.user_external_id, t.company_external_id,
           MIN(t.created_date_utc) AS first_custom_template_at
    FROM public.template t
    JOIN public.users tu ON tu.external_id = t.user_external_id
    WHERE t.created_date_utc > tu.created_at_utc + interval '5 minutes'
      AND t.is_archived = false
    GROUP BY t.user_external_id, t.company_external_id
),
billing_paid AS (
    SELECT company_external_id,
           MIN(subscription_started_date_utc) FILTER (
               WHERE billing_plan::text IN ('plus','pro')
           ) AS converted_to_paid_at,
           bool_or(billing_plan::text IN ('plus','pro')) AS on_paid_plan,
           (array_agg(billing_plan::text ORDER BY subscription_started_date_utc DESC NULLS LAST))[1] AS billing_plan
    FROM public.company_billing
    GROUP BY company_external_id
),
company_size AS (
    SELECT ur.company_external_id,
           COUNT(DISTINCT ur.user_external_id) AS marloo_active_user_count
    FROM public.user_roles ur
    WHERE ur.status = 'active'
    GROUP BY ur.company_external_id
),
demo_attendance AS (
    SELECT lower(att.email) AS user_email,
           MIN(d.created_date_utc) AS first_demo_date
    FROM reporting.demo d
    CROSS JOIN LATERAL unnest(d.attendee_emails) WITH ORDINALITY AS att(email, ord)
    WHERE (d.follow_up_context ->> 'is_demo') = 'true'
      AND d.organiser_email ILIKE '%@gomarloo.com'
    GROUP BY lower(att.email)
)
SELECT
    u.external_id            AS user_external_id,
    ur.company_external_id,
    u.email,
    u.created_at_utc         AS signed_up_at,
    ur.role::text            AS user_role,
    cmp.country_code,
    cs.marloo_active_user_count,
    cf.first_calendar_at     AS connected_calendar_at,
    bf.first_bot_meeting_at,
    tf.first_transcript_at,
    sm.first_synopsis_at,
    sm.second_synopsis_at,
    ch.first_meeting_chat_at,
    ct.first_custom_template_at,
    bp.converted_to_paid_at,
    bp.on_paid_plan,
    bp.billing_plan,
    (da.user_email IS NOT NULL) AS had_demo,
    da.first_demo_date
FROM public.user_roles ur
JOIN public.users u ON u.external_id = ur.user_external_id
LEFT JOIN public.companies cmp ON cmp.external_id = ur.company_external_id
LEFT JOIN calendar_first      cf ON cf.user_external_id    = ur.user_external_id
                                AND cf.company_external_id = ur.company_external_id
LEFT JOIN bot_first           bf ON bf.user_external_id    = ur.user_external_id
                                AND bf.company_external_id = ur.company_external_id
LEFT JOIN transcript_first    tf ON tf.user_external_id    = ur.user_external_id
                                AND tf.company_external_id = ur.company_external_id
LEFT JOIN synopsis_milestones sm ON sm.user_external_id    = ur.user_external_id
                                AND sm.company_external_id = ur.company_external_id
LEFT JOIN chat_first          ch ON ch.user_external_id    = ur.user_external_id
                                AND ch.company_external_id = ur.company_external_id
LEFT JOIN custom_template_first ct ON ct.user_external_id    = ur.user_external_id
                                  AND ct.company_external_id = ur.company_external_id
LEFT JOIN billing_paid        bp ON bp.company_external_id = ur.company_external_id
LEFT JOIN company_size        cs ON cs.company_external_id = ur.company_external_id
LEFT JOIN demo_attendance     da ON da.user_email          = lower(u.email)
WHERE ur.status = 'active'
  AND u.email NOT ILIKE '%@gomarloo.com'
ORDER BY u.created_at_utc DESC
`;

export type UserRowV2 = {
  user_external_id: string;
  company_external_id: string | null;
  email: string;
  created_at_utc: string;
  user_role: string | null;
  country_code: string | null;
  marloo_active_user_count: number;
  connected_calendar_at: string | null;
  first_bot_meeting_at: string | null;
  first_transcript_at: string | null;
  first_synopsis_at: string | null;
  second_synopsis_at: string | null;
  first_meeting_chat_at: string | null;
  first_custom_template_at: string | null;
  converted_to_paid_at: string | null;
  on_paid_plan: boolean;
  billing_plan: string | null;
  had_demo: boolean;
  first_demo_date: string | null;
};

const num = (v: unknown): number => {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
const str = (v: unknown): string => (v == null ? "" : String(v));
const strOrNull = (v: unknown): string | null => (v == null ? null : String(v));
const bool = (v: unknown): boolean => v === true || v === "t" || v === "true";

export function mapRows(columns: string[], rows: unknown[][]): UserRowV2[] {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  const get = (row: unknown[], k: string): unknown =>
    idx[k] !== undefined ? row[idx[k] as number] : undefined;
  return rows.map((r) => ({
    user_external_id:         str(get(r, "user_external_id")),
    company_external_id:      strOrNull(get(r, "company_external_id")),
    email:                    str(get(r, "email")),
    created_at_utc:           str(get(r, "signed_up_at")),
    user_role:                strOrNull(get(r, "user_role")),
    country_code:             strOrNull(get(r, "country_code")),
    marloo_active_user_count: num(get(r, "marloo_active_user_count")),
    connected_calendar_at:    strOrNull(get(r, "connected_calendar_at")),
    first_bot_meeting_at:     strOrNull(get(r, "first_bot_meeting_at")),
    first_transcript_at:      strOrNull(get(r, "first_transcript_at")),
    first_synopsis_at:        strOrNull(get(r, "first_synopsis_at")),
    second_synopsis_at:       strOrNull(get(r, "second_synopsis_at")),
    first_meeting_chat_at:    strOrNull(get(r, "first_meeting_chat_at")),
    first_custom_template_at: strOrNull(get(r, "first_custom_template_at")),
    converted_to_paid_at:     strOrNull(get(r, "converted_to_paid_at")),
    on_paid_plan:             bool(get(r, "on_paid_plan")),
    billing_plan:             strOrNull(get(r, "billing_plan")),
    had_demo:                 bool(get(r, "had_demo")),
    first_demo_date:          strOrNull(get(r, "first_demo_date")),
  }));
}
